import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatAd, formatBs } from './calendar';

// ── South Asian number format: 1,23,456 (last 3 digits, then pairs) ─────────────
function groupSouthAsian(digits) {
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
}
export function formatNprSouthAsian(n) {
  const num = Math.round(Number(n) || 0);
  const sign = num < 0 ? '-' : '';
  return `NPR ${sign}${groupSouthAsian(Math.abs(num).toString())}`;
}

// Load a (possibly remote) image into a PNG data URL. Resolves null on any failure
// (missing URL, CORS taint, load error) so the PDF degrades to text-only gracefully.
function loadImageDataUrl(url) {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

const fmtMonY = (d) => new Date(d).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
const pctChange = (cur, prior) => {
  if (!prior) return cur ? 'new' : '0%';
  const p = Math.round(((cur - prior) / Math.abs(prior)) * 100);
  return `${p >= 0 ? '+' : ''}${p}%`;
};

const RED  = [226, 75, 74];   // brand red
const DARK = [31, 41, 55];    // gray-800
const MUTED = [107, 114, 128]; // gray-500

/**
 * Generate and download a branded yearly P&L PDF. Client-side only (jsPDF + autotable).
 * @param data     yearly breakdown { year, calendar, months[], priorYearMonths[], totals, priorYearTotals }
 * @param club     from useTenant(): { name, slug, logo, pan, address }
 * @param user     from useAuth(): { name, email }
 * @param calendar 'ad' | 'bs'
 */
export async function exportYearlyFinanceToPDF(data, club, user, calendar) {
  const cal = calendar || data.calendar || 'ad';
  const isBs = cal === 'bs';
  const { year, months, totals, priorYearMonths, priorYearTotals } = data;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 14; // page margin

  // ── HEADER BAND ──────────────────────────────────────────────────────
  const logo = await loadImageDataUrl(club?.logo);
  if (logo) {
    const maxH = 18;
    const w = (logo.w / logo.h) * maxH;
    try { doc.addImage(logo.dataUrl, 'PNG', M, 10, Math.min(w, 40), maxH); } catch { /* skip */ }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...DARK);
  doc.text(club?.name || 'Club', pageW / 2, 16, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text('Financial Statement', pageW / 2, 22, { align: 'center' });

  // Top-right: PAN + address (each omitted if empty)
  let ry = 12;
  doc.setFontSize(9);
  if (club?.pan) { doc.text(`PAN: ${club.pan}`, pageW - M, ry, { align: 'right' }); ry += 5; }
  const addr = club?.address
    ? [club.address.street, club.address.city, club.address.district].filter(Boolean).join(', ')
    : '';
  if (addr) doc.text(addr, pageW - M, ry, { align: 'right' });

  // Header bottom border
  doc.setDrawColor(...MUTED);
  doc.setLineWidth(0.3);
  doc.line(M, 32, pageW - M, 32);

  // ── PERIOD BLOCK ─────────────────────────────────────────────────────
  const firstLabel = months[0]?.monthLabel || '';
  const lastLabel  = months[11]?.monthLabel || '';
  let periodStr;
  if (isBs) {
    const adRange = (months[0]?.startDate && months[11]?.endDate)
      ? ` (${fmtMonY(months[0].startDate)} – ${fmtMonY(months[11].endDate)})`
      : '';
    periodStr = `${firstLabel} – ${lastLabel}${adRange}`;
  } else {
    periodStr = `${firstLabel} – ${lastLabel}`;
  }

  doc.setFontSize(9);
  doc.setTextColor(...DARK);
  doc.text(`Period:  ${periodStr}`, M, 40);
  const now = new Date();
  doc.setTextColor(...MUTED);
  doc.text(`Generated: ${formatAd(now)} / ${formatBs(now)}`, pageW - M, 40, { align: 'right' });

  // ── YEAR TOTALS CARDS ────────────────────────────────────────────────
  const cards = [
    { label: 'Total Income',   value: formatNprSouthAsian(totals.income),   neg: false },
    { label: 'Total Expenses', value: formatNprSouthAsian(totals.expenses), neg: false },
    { label: 'Net Profit',     value: formatNprSouthAsian(totals.net),      neg: totals.net < 0 },
    { label: 'Margin',         value: totals.income === 0 ? '—' : `${totals.margin.toFixed(1)}%`, neg: false },
  ];
  const gap = 4;
  const cardW = (pageW - 2 * M - gap * 3) / 4;
  const cardY = 46;
  const cardH = 20;
  cards.forEach((c, i) => {
    const x = M + i * (cardW + gap);
    doc.setDrawColor(220);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, cardY, cardW, cardH, 1.5, 1.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(c.label.toUpperCase(), x + 3, cardY + 6);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...(c.neg ? RED : DARK));
    doc.text(c.value, x + 3, cardY + 14);
  });

  // ── MONTHLY BREAKDOWN TABLE ──────────────────────────────────────────
  const netCell = (val, blank) => blank
    ? { content: '—', styles: { halign: 'right', textColor: MUTED } }
    : { content: formatNprSouthAsian(val), styles: { halign: 'right', textColor: val < 0 ? RED : DARK } };

  const body = months.map(m => {
    const blank = m.income === 0 && m.expenses === 0;
    return [
      m.monthLabel,
      blank ? '—' : formatNprSouthAsian(m.income),
      blank ? '—' : formatNprSouthAsian(m.expenses),
      netCell(m.net, blank),
      blank || m.income === 0 ? '—' : `${m.margin.toFixed(1)}%`,
    ];
  });

  autoTable(doc, {
    startY: cardY + cardH + 6,
    head: [['Month', 'Income (NPR)', 'Expenses (NPR)', 'Net (NPR)', 'Margin (%)']],
    body,
    foot: [[
      'TOTAL',
      formatNprSouthAsian(totals.income),
      formatNprSouthAsian(totals.expenses),
      netCell(totals.net, false),
      totals.income === 0 ? '—' : `${totals.margin.toFixed(1)}%`,
    ]],
    theme: 'grid',
    margin: { left: M, right: M },
    styles: { fontSize: 9, cellPadding: 2, lineColor: [229, 231, 235], textColor: DARK },
    headStyles: { fillColor: [243, 244, 246], textColor: DARK, fontStyle: 'bold' },
    footStyles: { fillColor: [249, 250, 251], textColor: DARK, fontStyle: 'bold' },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
  });

  // ── YEAR-OVER-YEAR (only if prior year has data) ─────────────────────
  const priorHasData = priorYearTotals.income > 0 || priorYearTotals.expenses > 0;
  let y = (doc.lastAutoTable?.finalY || cardY + cardH + 60) + 8;
  if (priorHasData) {
    const priorLabel = `${year - 1}${isBs ? ' BS' : ''}`;
    const netDiff = totals.net - priorYearTotals.net;
    const lines = [
      `vs Last Year (${priorLabel}):`,
      `Income: ${pctChange(totals.income, priorYearTotals.income)} (${formatNprSouthAsian(priorYearTotals.income)} → ${formatNprSouthAsian(totals.income)})`,
      `Expenses: ${pctChange(totals.expenses, priorYearTotals.expenses)}`,
      `Net Profit: ${netDiff >= 0 ? 'improved by' : 'declined by'} ${formatNprSouthAsian(Math.abs(netDiff))}`,
    ];
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.text(lines[0], M, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    lines.slice(1).forEach((ln, i) => doc.text(ln, M, y + 5 + i * 5));
  }

  // ── FOOTER ───────────────────────────────────────────────────────────
  const fy = pageH - 12;
  doc.setDrawColor(...MUTED);
  doc.setLineWidth(0.2);
  doc.line(M, fy - 4, pageW - M, fy - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(`Generated by ${user?.name || 'Admin'} · ${user?.email || ''}`, M, fy);
  doc.text('Page 1 of 1', pageW / 2, fy, { align: 'center' });
  doc.text('TrainAxis · trainaxis.app', pageW - M, fy, { align: 'right' });

  const slug = club?.slug || 'club';
  doc.save(`trainaxis-pnl-${slug}-${year}-${cal}.pdf`);
}
